import { useEffect, useMemo, useState } from 'react';
import { streamsApi } from '../api';
import './TopStreamers.css';

function TopStreamers() {
  const [streamers, setStreamers] = useState([]);
  const [voteInfo, setVoteInfo] = useState({});
  const [now, setNow] = useState(Date.now());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activePlatform, setActivePlatform] = useState('all');

  const isAuthenticated = !!localStorage.getItem('discord_token');

  const platforms = [
    { key: 'all', label: 'Alle', icon: '🌐' },
    { key: 'twitch', label: 'Twitch', icon: '🟣' },
    { key: 'youtube', label: 'YouTube', icon: '🔴' },
    { key: 'tiktok', label: 'TikTok', icon: '⚫' },
    { key: 'kick', label: 'Kick', icon: '🟢' },
  ];

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    streamsApi.getAll()
      .then(data => {
        setStreamers(data);
        if (isAuthenticated) {
          Promise.all(
            data.map(s =>
              streamsApi.getMyVote(s.id)
                .then(r => ({ id: s.id, ...r }))
                .catch(() => ({ id: s.id, voted: false, cooldown: false, next_vote_at: null }))
            )
          ).then(results => {
            const map = {};
            results.forEach(r => { map[r.id] = r; });
            setVoteInfo(map);
          });
        }
      })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  const getViewerCount = (streamer) => {
    if (streamer.platform === 'tiktok' && streamer.tiktok_viewers) {
      return streamer.tiktok_viewers;
    }
    return streamer.peak_viewers || 0;
  };

  const getStreamUrl = (streamer) => {
    if (streamer.platform === 'tiktok' && streamer.tiktok_link) return streamer.tiktok_link;
    if (streamer.platform === 'twitch' && streamer.twitch_link) return streamer.twitch_link;
    if (streamer.platform === 'youtube' && streamer.youtube_link) return streamer.youtube_link;
    if (streamer.platform === 'tiktok' && streamer.tiktok_username) {
      return `https://www.tiktok.com/@${streamer.tiktok_username.replace('@', '')}/live`;
    }
    if (streamer.platform === 'twitch' && streamer.channel) {
      return `https://www.twitch.tv/${streamer.channel}`;
    }
    return null;
  };

  const getTwitchUrl = (streamer) => {
    if (streamer.platform === 'twitch' && streamer.twitch_link) return streamer.twitch_link;
    if (streamer.platform === 'twitch' && streamer.channel) {
      return `https://www.twitch.tv/${streamer.channel}`;
    }
    return null;
  };

  const getPlatformIcon = (platform) => {
    switch (platform) {
      case 'twitch': return '🟣';
      case 'youtube': return '🔴';
      case 'tiktok': return '⚫';
      case 'kick': return '🟢';
      default: return '📺';
    }
  };

  const getStatusBadge = (status) => {
    if (status === 'live') {
      return <span style={{ color: '#e94560', fontWeight: 'bold' }}>● LIVE</span>;
    }
    return <span style={{ color: '#64748b' }}>Offline</span>;
  };

  const formatCountdown = (ms) => {
    if (ms <= 0) return '00:00:00';
    const total = Math.floor(ms / 1000);
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  const formatClock = (date) => {
    return date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  };

  const handleVote = async (streamer) => {
    if (!isAuthenticated) {
      setError('Bitte melde dich mit Discord an, um zu voten.');
      return;
    }
    const info = voteInfo[streamer.id];
    if (info?.cooldown) {
      const at = info.next_vote_at ? new Date(info.next_vote_at).toLocaleString('de-DE') : 'später';
      setError(`Nächster Vote möglich um ${at}.`);
      return;
    }
    try {
      const result = await streamsApi.vote(streamer.id);
      const nextAt = new Date(now + 12 * 60 * 60 * 1000).toISOString();
      setStreamers(prev => prev.map(s => s.id === streamer.id ? { ...s, vote_count: result.vote_count } : s));
      setVoteInfo(prev => ({ ...prev, [streamer.id]: { voted: true, cooldown: true, next_vote_at: nextAt } }));
      setError(null);
    } catch (e) {
      const data = e.response?.data;
      if (data?.error === 'cooldown') {
        setVoteInfo(prev => ({ ...prev, [streamer.id]: { voted: true, cooldown: true, next_vote_at: data.next_vote_at } }));
        const at = data.next_vote_at ? new Date(data.next_vote_at).toLocaleString('de-DE') : 'später';
        setError(`Nächster Vote möglich um ${at}.`);
      } else {
        setError(data?.error || e.message);
      }
    }
  };

  const sorted = [...streamers]
    .filter(s => activePlatform === 'all' || s.platform === activePlatform)
    .sort((a, b) => (b.vote_count || 0) - (a.vote_count || 0));

  const counts = streamers.reduce((acc, s) => {
    acc.all = (acc.all || 0) + 1;
    acc[s.platform] = (acc[s.platform] || 0) + 1;
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="page">
        <div className="loading-container">
          <div className="spinner" />
          <p>Lädt...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <h1>Top Streamer ({sorted.length})</h1>
      <p className="muted">Sortiert nach Community-Votes. Du kannst alle 12 Stunden 1× pro Streamer voten.</p>
      <div className="platform-filter">
        {platforms.map(p => {
          const count = counts[p.key] || 0;
          return (
            <button
              key={p.key}
              onClick={() => setActivePlatform(p.key)}
              className={`platform-chip ${activePlatform === p.key ? 'active' : ''}`}
              disabled={p.key !== 'all' && count === 0}
            >
              <span className="platform-chip-icon">{p.icon}</span>
              <span>{p.label}</span>
              <span className="platform-chip-count">{count}</span>
            </button>
          );
        })}
      </div>
      {error && <p style={{ color: '#e94560' }}>{error}</p>}
      {sorted.length === 0 ? (
        <p className="muted">Keine Streamer gefunden.</p>
      ) : (
        <div className="streamers-list">
          {sorted.map((streamer, index) => {
            const streamUrl = getStreamUrl(streamer);
            const viewers = getViewerCount(streamer);
            const info = voteInfo[streamer.id];
            const nextAt = info?.next_vote_at ? new Date(info.next_vote_at) : null;
            const onCooldown = !!(nextAt && nextAt.getTime() > now);
            const remainingMs = nextAt ? nextAt.getTime() - now : 0;
            return (
              <div key={streamer.id} className="streamer-row">
                <span className="streamer-rank">#{index + 1}</span>
                <div className="streamer-info">
                  <h3>
                    {streamUrl ? (
                      <a href={streamUrl} target="_blank" rel="noreferrer" style={{ color: 'var(--text-primary)', textDecoration: 'none' }}>
                        {getPlatformIcon(streamer.platform)} {streamer.display_name || streamer.username}
                      </a>
                    ) : (
                      <>{getPlatformIcon(streamer.platform)} {streamer.display_name || streamer.username}</>
                    )}
                  </h3>
                  <div className="streamer-links">
                    <p className="muted">
                      {streamer.platform?.toUpperCase()}
                    </p>
                    {getTwitchUrl(streamer) && (
                      <a
                        href={getTwitchUrl(streamer)}
                        target="_blank"
                        rel="noreferrer"
                        className="streamer-twitch-btn"
                        title="Twitch öffnen"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714z"/>
                        </svg>
                        Twitch
                      </a>
                    )}
                  </div>
                </div>
                <span className="streamer-viewers">
                  {getStatusBadge(streamer.status === 'live' || streamer.tiktok_status === 'live' ? 'live' : 'offline')}
                  {viewers > 0 && <span style={{ marginLeft: '8px', color: '#94a3b8' }}>👁 {viewers}</span>}
                </span>
                <div className="streamer-vote">
                  {onCooldown ? (
                    <div className="vote-cooldown">
                      <span className="vote-count">★ {streamer.vote_count || 0}</span>
                      <span className="vote-cooldown-text">
                        Nächster Vote möglich in {formatCountdown(remainingMs)} (um {formatClock(nextAt)})
                      </span>
                    </div>
                  ) : (
                    <button
                      onClick={() => handleVote(streamer)}
                      className="vote-btn"
                      title={isAuthenticated ? 'Voten' : 'Mit Discord anmelden'}
                    >
                      ★ {streamer.vote_count || 0}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default TopStreamers;
